/**
 * The time model — US 03.06, pure. Content gates on `daySinceStart`,
 * 0-indexed PER PLAYER, advancing at the player's LOCAL midnight — never on
 * calendar days, which silently lock out late joiners. Cutover stays a
 * single global UTC instant, independent of any player's counter.
 */

/** Calendar date (Y-M-D) of an instant in a given IANA timezone. */
function localDate(ms: number, timeZone: string): string {
  try {
    // en-CA gives ISO-ordered YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date(ms));
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC' }).format(new Date(ms));
  }
}

/**
 * Whole local midnights crossed since the visit started. Day 0 is the
 * arrival day, whatever the hour; the counter ticks at local midnight.
 */
export function daySinceStart(startedAtIso: string, nowMs: number, timeZone: string): number {
  const start = localDate(Date.parse(startedAtIso), timeZone);
  const today = localDate(nowMs, timeZone);
  const diff = Math.round((Date.parse(today) - Date.parse(start)) / 86_400_000);
  return Math.max(0, diff);
}

/** Day of week (0 = Sunday, JS convention) in the player's timezone. */
export function localDayOfWeek(nowMs: number, timeZone: string): number {
  // The local calendar date at that instant, interpreted as UTC midnight,
  // has the weekday we want — reuses the same Intl path as daySinceStart.
  return new Date(Date.parse(localDate(nowMs, timeZone))).getUTCDay();
}

/** Days until the global cutover instant, or null for untimed cities. */
export function daysUntilCutover(
  scheduledStartIso: string | null,
  durationDays: number,
  nowMs: number,
): number | null {
  if (!scheduledStartIso) return null;
  const end = Date.parse(scheduledStartIso) + durationDays * 86_400_000;
  return Math.max(0, Math.ceil((end - nowMs) / 86_400_000));
}

// ------------------------------------------------------------------ hazards

export interface HazardDef {
  id: string;
  /** Tile-coordinate waypoints; the car glides along the polyline. */
  path: { x: number; y: number }[];
  speedTilesPerSec: number;
  /** A new car spawns every interval; phase is derived from wall clock. */
  spawnIntervalMs: number;
  damage: number;
}

export const INVULNERABILITY_MS = 800; // 108

/**
 * Server-side gate for a reported hazard hit (US 03.07): the hazard must
 * exist in the city, and the 800ms invulnerability window is enforced HERE —
 * the client's flash is presentation, this is the law.
 */
export function validateHazardHit(
  hazards: readonly HazardDef[],
  hazardId: string,
  lastHitAtMs: number | undefined,
  nowMs: number,
): { ok: true; damage: number } | { ok: false; reason: 'UNKNOWN_HAZARD' | 'INVULNERABLE' } {
  const hazard = hazards.find((h) => h.id === hazardId);
  if (!hazard) return { ok: false, reason: 'UNKNOWN_HAZARD' };
  if (lastHitAtMs !== undefined && nowMs - lastHitAtMs < INVULNERABILITY_MS) {
    return { ok: false, reason: 'INVULNERABLE' };
  }
  return { ok: true, damage: hazard.damage };
}

/**
 * Deterministic car position for a hazard at a wall-clock instant — pure so
 * the client renders and tests verify the same maths. Returns null when no
 * car is on the path (between spawns), plus the seconds until the next
 * spawn (drives the ≥1s warning tell).
 */
export function carAt(
  hazard: HazardDef,
  nowMs: number,
): { x: number; y: number; secondsToNextSpawn: number } | null {
  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < hazard.path.length; i++) {
    const a = hazard.path[i - 1]!;
    const b = hazard.path[i]!;
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    seg.push(d);
    total += d;
  }
  const runMs = (total / hazard.speedTilesPerSec) * 1000;
  const phase = nowMs % hazard.spawnIntervalMs;
  const secondsToNextSpawn = (hazard.spawnIntervalMs - phase) / 1000;
  if (phase > runMs) return null; // car finished; road empty until next spawn
  let dist = (phase / 1000) * hazard.speedTilesPerSec;
  for (let i = 1; i < hazard.path.length; i++) {
    const a = hazard.path[i - 1]!;
    const b = hazard.path[i]!;
    const d = seg[i - 1]!;
    if (dist <= d) {
      const t = d === 0 ? 0 : dist / d;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, secondsToNextSpawn };
    }
    dist -= d;
  }
  const last = hazard.path[hazard.path.length - 1]!;
  return { x: last.x, y: last.y, secondsToNextSpawn };
}
